import type { walletMessage } from "@defuse-protocol/internal-utils"
import { QuoteRequest } from "@defuse-protocol/one-click-sdk-typescript"
import type { TokenInfo } from "@src/components/DefuseSDK/types/base"
import { assert } from "@src/components/DefuseSDK/utils/assert"
import { useIs1CsEnabled } from "@src/hooks/useIs1CsEnabled"
import { FeatureFlagsContext } from "@src/providers/FeatureFlagsProvider"
import { usePrivateModeStore } from "@src/stores/usePrivateModeStore"
import { useSlippageStore } from "@src/stores/useSlippageStore"
import { getAppFeeRecipients } from "@src/utils/getAppFeeRecipient"
import { createActorContext } from "@xstate/react"
import { useEffect } from "react"
import type { PropsWithChildren, ReactElement, ReactNode } from "react"
import { useContext } from "react"
import { useFormContext } from "react-hook-form"
import {
  type Actor,
  type ActorOptions,
  type SnapshotFrom,
  fromPromise,
} from "xstate"
import { swapIntent1csMachine } from "../../machines/swapIntent1csMachine"
import { swapIntentMachine } from "../../machines/swapIntentMachine"
import { swapUIMachine } from "../../machines/swapUIMachine"
import { useSwapTokenChangeNotifier } from "../hooks/useTokenChangeNotifier"
import type { SwapFormValues } from "./SwapForm"

/**
 * We explicitly define the type of `swapUIMachine` to avoid:
 * ```
 * TS7056: The inferred type of this node exceeds the maximum length the
 * compiler will serialize. An explicit type annotation is needed.
 * ```
 *
 * Either the machine type is too complex or we incorrectly specify types inside it.
 * Either way it is just a workaround for TypeScript limitations.
 * Copy-paste the type from `@xstate/react/dist/declarations/src/createActorContext.d.ts`
 */
interface SwapUIMachineContextInterface {
  useSelector: <T>(
    selector: (snapshot: SnapshotFrom<typeof swapUIMachine>) => T,
    compare?: (a: T, b: T) => boolean
  ) => T
  useActorRef: () => Actor<typeof swapUIMachine>
  Provider: (props: {
    children: ReactNode
    options?: ActorOptions<typeof swapUIMachine>
    /** @deprecated Use `logic` instead. */
    machine?: never
    logic?: typeof swapUIMachine
    // biome-ignore lint/suspicious/noExplicitAny: it is fine `any` here
  }) => ReactElement<any, any>
}

export const SwapUIMachineContext: SwapUIMachineContextInterface =
  createActorContext(swapUIMachine)

interface SwapUIMachineProviderProps extends PropsWithChildren {
  initialTokenIn?: TokenInfo
  initialTokenOut?: TokenInfo
  tokenList: TokenInfo[]
  signMessage: (
    params: walletMessage.WalletMessage
  ) => Promise<walletMessage.WalletSignatureResult | null>
  referral?: string
}

export function SwapUIMachineProvider({
  children,
  initialTokenIn,
  initialTokenOut,
  tokenList,
  signMessage,
  referral,
}: SwapUIMachineProviderProps) {
  const { setValue } = useFormContext<SwapFormValues>()
  const { whitelabelTemplate } = useContext(FeatureFlagsContext)
  const appFeeRecipients = getAppFeeRecipients(whitelabelTemplate)
  const tokenIn = initialTokenIn || tokenList[0]
  const tokenOut = initialTokenOut || tokenList[1]
  const is1cs = useIs1CsEnabled()
  const isConfidential = usePrivateModeStore(
    (state) => state.isPrivateModeEnabled
  )
  assert(tokenIn && tokenOut, "TokenIn and TokenOut must be defined")

  return (
    <SwapUIMachineContext.Provider
      // re-initialize the provider when the is1cs prop changes
      key={`${is1cs ? "1cs" : "not1cs"}-${isConfidential ? "confidential" : "public"}`}
      options={{
        input: {
          tokenIn,
          tokenOut,
          tokenList,
          referral,
          is1cs,
          isConfidential,
          appFeeRecipients,
        },
      }}
      logic={swapUIMachine.provide({
        actions: {
          updateUIAmount: ({ context }) => {
            const quote = context.quote
            const isExactInput =
              context.formValues.swapType === QuoteRequest.swapType.EXACT_INPUT
            const fieldNameToUpdate = isExactInput ? "amountOut" : "amountIn"
            if (quote === null || quote.tag === "err") {
              setValue(
                fieldNameToUpdate,
                context.formValues[fieldNameToUpdate],
                { shouldValidate: false }
              )
              return
            }
            // Extra protection from setting anything when both form values are empty
            if (
              context.formValues.amountIn === "" &&
              context.formValues.amountOut === ""
            )
              return
            setValue(fieldNameToUpdate, context.formValues[fieldNameToUpdate], {
              shouldValidate: true,
            })
          },
        },
        actors: {
          swapActor: swapIntentMachine.provide({
            actors: {
              signMessage: fromPromise(({ input }) => signMessage(input)),
            },
          }),
          swap1csActor: swapIntent1csMachine.provide({
            actors: {
              signMessage: fromPromise(({ input }) => signMessage(input)),
            },
          }),
        },
      })}
    >
      <SlippageInitializer />
      <TokenChangeNotifier tokenIn={tokenIn} tokenOut={tokenOut} />
      {children}
    </SwapUIMachineContext.Provider>
  )
}

function SlippageInitializer() {
  const actorRef = SwapUIMachineContext.useActorRef()
  const getSlippageBasisPoints = useSlippageStore(
    (state) => state.getSlippageBasisPoints
  )
  const currentSlippage = SwapUIMachineContext.useSelector(
    (state) => state.context.slippageBasisPoints
  )

  useEffect(() => {
    const storedSlippage = getSlippageBasisPoints()
    if (storedSlippage !== currentSlippage) {
      actorRef.send({
        type: "SET_SLIPPAGE",
        params: { slippageBasisPoints: storedSlippage },
      })
    }
  }, [actorRef, getSlippageBasisPoints, currentSlippage])

  return null
}

function TokenChangeNotifier({
  tokenIn,
  tokenOut,
}: {
  tokenIn: TokenInfo
  tokenOut: TokenInfo
}) {
  useSwapTokenChangeNotifier({ tokenIn, tokenOut })
  return null
}
